import { Mp3Encoder } from '@breezystack/lamejs';
import { Observable, Subscriber } from 'rxjs';

const MICROPHONE_WORKLET_NAME = 'microphone-stream-processor';
const MP3_SAMPLES_PER_FRAME = 1152;
const DEFAULT_MP3_BIT_RATE = 64;
const MICROPHONE_WORKLET_SOURCE = `
class MicrophoneStreamProcessor extends AudioWorkletProcessor {
  process(inputs) {
    const input = inputs[0];
    const channel = input && input[0];

    if (channel && channel.length) {
      this.port.postMessage(channel.slice(0));
    }

    return true;
  }
}

registerProcessor('${MICROPHONE_WORKLET_NAME}', MicrophoneStreamProcessor);
`;

export interface MicrophoneProcessorConfig {
    maxRecordTime: number;
    bitRate?: number;
}

interface ActiveRecordingSession {
    subscriber: Subscriber<Blob>;
    stream: MediaStream;
    audioContext: AudioContext;
    sourceNode: MediaStreamAudioSourceNode;
    workletNode: AudioWorkletNode;
    silentGainNode: GainNode;
    encoder: Mp3Encoder;
    mp3Chunks: Uint8Array[];
    pendingSamples: Int16Array;
    limitTimerId: number;
    isStopped: boolean;
}

export class LimitRecordException extends Error {
    private readonly trackFile: Blob;

    public constructor(trackFile: Blob) {
        super('Превышено максимальное время записи');
        this.name = 'LimitRecordException';
        this.trackFile = trackFile;
        Object.setPrototypeOf(this, new.target.prototype);
    }

    /**
     * Возвращает MP3-файл, собранный к моменту превышения лимита записи.
     */
    public getTrackFile(): Blob {
        return this.trackFile;
    }
}

/**
 * Управляет записью микрофона и кодирует поток PCM в MP3 через AudioWorklet.
 */
export class MicrophoneProcessor {
    private readonly maxRecordTime: number;
    private readonly bitRate: number;
    private activeSession: ActiveRecordingSession | null = null;
    private workletModuleUrl: string | null = null;
    private isDestroyed = false;

    public constructor(config: MicrophoneProcessorConfig) {
        if (!config?.maxRecordTime || config.maxRecordTime <= 0) {
            throw new Error('maxRecordTime должен быть больше 0');
        }

        this.maxRecordTime = config.maxRecordTime;
        this.bitRate = config.bitRate ?? DEFAULT_MP3_BIT_RATE;
    }

    /**
     * Запускает запись микрофона и возвращает Observable с итоговым MP3 Blob.
     * При отписке запись останавливается и ресурсы освобождаются без эмита результата.
     */
    public record(): Observable<Blob> {
        return new Observable<Blob>((subscriber) => {
            if (this.isDestroyed) {
                subscriber.error(new Error('MicrophoneProcessor уже уничтожен'));
                return undefined;
            }

            if (this.activeSession !== null) {
                subscriber.error(new Error('Запись уже выполняется'));
                return undefined;
            }

            let isDisposed = false;

            void this.startRecording(subscriber)
                .catch((error: unknown) => {
                    if (isDisposed || subscriber.closed) {
                        return;
                    }
                    subscriber.error(error instanceof Error ? error : new Error('Не удалось начать запись микрофона'));
                });

            return () => {
                isDisposed = true;
                void this.stopActiveSession();
            };
        });
    }

    /**
     * Останавливает активную запись и возвращает накопленный MP3 Blob.
     * Если запись не была начата или уже остановлена, возвращает null.
     */
    public stop(): Promise<Blob | null> {
        const session = this.activeSession;
        if (session === null) {
            return Promise.resolve(null);
        }
        return this.stopSession(session);
    }

    /**
     * Останавливает активную запись и освобождает все занятые браузерные ресурсы.
     */
    public destroy(): void {
        this.isDestroyed = true;
        void this.stopActiveSession();

        if (this.workletModuleUrl !== null) {
            URL.revokeObjectURL(this.workletModuleUrl);
            this.workletModuleUrl = null;
        }
    }

    private async startRecording(subscriber: Subscriber<Blob>): Promise<void> {
        this.assertRecordingSupport();

        const stream = await navigator.mediaDevices.getUserMedia({
            audio: {
                channelCount: 1,
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
            },
            video: false
        });

        try {
            const audioContext = this.createAudioContext();
            const sourceNode = audioContext.createMediaStreamSource(stream);
            await audioContext.audioWorklet.addModule(this.getWorkletModuleUrl());

            const workletNode = new AudioWorkletNode(audioContext, MICROPHONE_WORKLET_NAME);
            const silentGainNode = audioContext.createGain();
            silentGainNode.gain.value = 0;

            sourceNode.connect(workletNode);
            workletNode.connect(silentGainNode);
            silentGainNode.connect(audioContext.destination);

            if (audioContext.state === 'suspended') {
                await audioContext.resume();
            }

            const session: ActiveRecordingSession = {
                subscriber,
                stream,
                audioContext,
                sourceNode,
                workletNode,
                silentGainNode,
                encoder: new Mp3Encoder(1, audioContext.sampleRate, this.bitRate),
                mp3Chunks: [],
                pendingSamples: new Int16Array(0),
                limitTimerId: window.setTimeout(() => {
                    void this.handleRecordLimitReached(session);
                }, this.maxRecordTime),
                isStopped: false
            };

            workletNode.port.onmessage = (event: MessageEvent<Float32Array>) => {
                this.handleAudioChunk(session, event.data);
            };

            this.activeSession = session;
        } catch (error) {
            stream.getTracks().forEach(track => track.stop());
            throw error;
        }
    }

    private handleAudioChunk(session: ActiveRecordingSession, chunk: Float32Array): void {
        if (session.isStopped || chunk.length === 0) {
            return;
        }

        const pcmChunk = this.convertFloat32ToInt16(chunk);
        session.pendingSamples = this.mergeInt16Arrays(session.pendingSamples, pcmChunk);

        while (session.pendingSamples.length >= MP3_SAMPLES_PER_FRAME) {
            const frame = session.pendingSamples.subarray(0, MP3_SAMPLES_PER_FRAME);
            const encodedChunk = session.encoder.encodeBuffer(frame);

            if (encodedChunk.length > 0) {
                session.mp3Chunks.push(encodedChunk);
            }

            session.pendingSamples = session.pendingSamples.subarray(MP3_SAMPLES_PER_FRAME);
        }
    }

    private async handleRecordLimitReached(session: ActiveRecordingSession): Promise<void> {
        if (this.activeSession !== session) {
            return;
        }

        const trackFile = await this.stopSession(session);
        if (trackFile === null || session.subscriber.closed) {
            return;
        }

        session.subscriber.error(new LimitRecordException(trackFile));
    }

    private async stopActiveSession(): Promise<void> {
        const session = this.activeSession;
        if (session === null) {
            return;
        }

        await this.stopSession(session);
    }

    private async stopSession(session: ActiveRecordingSession): Promise<Blob | null> {
        if (session.isStopped) {
            return null;
        }

        session.isStopped = true;
        if (this.activeSession === session) {
            this.activeSession = null;
        }

        window.clearTimeout(session.limitTimerId);
        session.workletNode.port.onmessage = null;

        try {
            session.sourceNode.disconnect();
            session.workletNode.disconnect();
            session.silentGainNode.disconnect();
        } catch {
            // Ноды уже могли быть отсоединены во время teardown.
        }

        session.stream.getTracks().forEach(track => track.stop());

        if (session.pendingSamples.length > 0) {
            const tailChunk = session.encoder.encodeBuffer(session.pendingSamples);
            if (tailChunk.length > 0) {
                session.mp3Chunks.push(tailChunk);
            }
        }

        const flushChunk = session.encoder.flush();
        if (flushChunk.length > 0) {
            session.mp3Chunks.push(flushChunk);
        }

        await session.audioContext.close().catch(() => undefined);

        return new Blob(session.mp3Chunks as unknown as BlobPart[], { type: 'audio/mpeg' });
    }

    private assertRecordingSupport(): void {
        if (!navigator.mediaDevices?.getUserMedia) {
            throw new Error('Браузер не поддерживает доступ к микрофону');
        }

        if (typeof AudioWorkletNode === 'undefined') {
            throw new Error('Браузер не поддерживает AudioWorklet');
        }
    }

    private createAudioContext(): AudioContext {
        const AudioContextConstructor = window.AudioContext
            ?? (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

        if (!AudioContextConstructor) {
            throw new Error('Браузер не поддерживает AudioContext');
        }

        return new AudioContextConstructor();
    }

    private getWorkletModuleUrl(): string {
        if (this.workletModuleUrl === null) {
            const workletBlob = new Blob([MICROPHONE_WORKLET_SOURCE], { type: 'text/javascript' });
            this.workletModuleUrl = URL.createObjectURL(workletBlob);
        }

        return this.workletModuleUrl;
    }

    private convertFloat32ToInt16(input: Float32Array): Int16Array {
        const result = new Int16Array(input.length);

        for (let index = 0; index < input.length; index++) {
            const sample = Math.max(-1, Math.min(1, input[index]));
            result[index] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
        }

        return result;
    }

    private mergeInt16Arrays(first: Int16Array, second: Int16Array): Int16Array {
        if (first.length === 0) {
            return second;
        }

        if (second.length === 0) {
            return first;
        }

        const merged = new Int16Array(first.length + second.length);
        merged.set(first, 0);
        merged.set(second, first.length);
        return merged;
    }
}
